import { api } from './client';
import type { BingDateGrain } from './bingDashboard';
import type { GoogleReportRow } from './googleDashboard';

export type MetaReportType = 'overview' | 'target' | 'new_old' | 'country' | 'new_customer' | 'detail';
export type MetaReportRow = GoogleReportRow;

export interface MetaOption {
  value: string;
  label: string;
}

export interface MetaReportOption extends MetaOption {
  dims: string[];
}

export interface MetaFilterOption {
  key: string;
  label: string;
  options: string[];
  width?: number;
}

export interface MetaTextFilterOption {
  key: string;
  label: string;
  width?: number;
}

export interface MetaPresetOption {
  value: string;
  label: string;
  dims: string[];
  report: MetaReportType;
}

export interface MetaDashboardOptions {
  date_grains: MetaOption[];
  reports: MetaReportOption[];
  dimensions: MetaOption[];
  detail_filter_options: MetaFilterOption[];
  text_filters: MetaTextFilterOption[];
  presets: MetaPresetOption[];
}

export interface MetaReportResponse {
  list: MetaReportRow[];
  summary: MetaReportRow | null;
  region: string;
  site_code: string;
  start_date: string;
  end_date: string;
  date_grain: BingDateGrain;
  report: MetaReportType;
}

export interface MetaReportParams {
  brand_id: string;
  site_id: string;
  start_date: string;
  end_date: string;
  date_grain: BingDateGrain;
  report: MetaReportType;
  dimensions?: string;
  target_type?: string;
  new_old_type?: string;
  country_type?: string;
  country?: string;
  campaign_type?: string;
  campaign_type_2?: string;
  product_posi?: string;
  sex_type?: string;
  product_material?: string;
  bid_type?: string;
  material_type?: string;
  price_type?: string;
  product_type?: string;
  new_customer_segment?: string;
  new_customer_segment_detail?: string;
  old_customer_segment?: string;
  old_customer_segment_detail?: string;
  campaign_id?: string;
  campaign_name?: string;
  adset_name?: string;
  ad_name?: string;
  ad_text_filters?: string;
  account_name?: string;
}

// Vooglam Meta uses a dedicated GuanData data set. Keep this contract
// separate from the Zeelool monitoring report above.
export type VooglamMetaDataset = 'main' | 'country';

export interface VooglamMetaReportParams {
  brand_id: string;
  site_id: string;
  start_date: string;
  end_date: string;
  date_grain?: BingDateGrain;
  dataset: VooglamMetaDataset;
  dimensions?: string;
  traffic_type?: string;
  user_segment_key?: string;
  account_name?: string;
  campaign_name_type?: string;
  creativity_type?: string;
  is_new_type?: string;
  uptime?: string;
  marketing_event?: string;
  image_type?: string;
  optimization_goal_val_cn?: string;
  bid_strategy_val_cn?: string;
  attribution_model?: string;
  country?: string;
  cn_country?: string;
  priority?: string;
}

export interface VooglamMetaReportResponse {
  list: GoogleReportRow[];
  summary: GoogleReportRow | null;
  region: string;
  site_code: string;
  start_date: string;
  end_date: string;
  date_grain: BingDateGrain;
  report: VooglamMetaDataset;
}

type ApiEnvelope<T> = { code: number; data: T; message?: string };

export const metaDashboardApi = {
  getOptions: () =>
    api.get<ApiEnvelope<MetaDashboardOptions>>('/ad-dashboard/meta/options'),
  getReport: (params: MetaReportParams) =>
    api.get<ApiEnvelope<MetaReportResponse>>('/ad-dashboard/meta/report', { params }),
  getVooglamReport: (params: VooglamMetaReportParams) =>
    api.get<ApiEnvelope<VooglamMetaReportResponse>>('/ad-dashboard/meta/v/report', { params }),
};
